<script setup lang="ts">
import { getDirectusAssetURL } from '@@/server/utils/directus-utils';
import { watch, ref } from 'vue';

interface DirectusVideoProps {
	uuid: string;
	title?: string | null;
	showTitle?: boolean;
	autoplay?: boolean;
	[key: string]: any;
}

const props = withDefaults(defineProps<DirectusVideoProps>(), {
	showTitle: true,
	autoplay: false,
});

const src = ref(getDirectusAssetURL(props.uuid));
const videoRef = ref<HTMLVideoElement | null>(null);

watch(
	() => props.uuid,
	(newUuid) => {
		src.value = getDirectusAssetURL(newUuid);
	},
);

// Play video when autoplay is enabled and video is ready
watch(
	() => [props.autoplay, videoRef.value],
	([autoplay, video]) => {
		if (autoplay && video && video.readyState >= 2) {
			video.play().catch((error) => {
				// Autoplay might fail due to browser policies, ignore silently
				console.debug('Autoplay prevented:', error);
			});
		}
	},
	{ immediate: true },
);

function handleLoadedData() {
	if (props.autoplay && videoRef.value) {
		// Ensure video is not muted
		videoRef.value.muted = false;
		videoRef.value.play().catch((error) => {
			console.debug('Autoplay prevented:', error);
		});
	}
}

function handlePlay() {
	// Ensure audio is unmuted when video plays
	if (videoRef.value) {
		videoRef.value.muted = false;
	}
}
</script>

<template>
	<div class="relative w-full h-full">
		<video
			ref="videoRef"
			:src="src"
			:autoplay="autoplay"
			controls
			controlsList="nodownload"
			preload="auto"
			playsinline
			class="w-full h-full object-cover video-controls-visible relative z-10"
			v-bind="{ ...props, uuid: undefined, title: undefined, showTitle: undefined, autoplay: undefined }"
			@loadeddata="handleLoadedData"
			@play="handlePlay"
		>
			Your browser does not support the video tag.
		</video>
		<div
			v-if="title && showTitle"
			class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-4 pt-4 pb-16 text-white pointer-events-none z-0"
		>
			<p class="text-sm font-medium">{{ title }}</p>
		</div>
	</div>
</template>

<style scoped>
.video-controls-visible::-webkit-media-controls-panel {
	display: flex !important;
	opacity: 1 !important;
}

.video-controls-visible::-webkit-media-controls {
	opacity: 1 !important;
}

.video-controls-visible::-webkit-media-controls-enclosure {
	opacity: 1 !important;
}
</style>

